/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { Spin, Switch, Tooltip, Button, Alert, Drawer } from 'antd';

import CalculateCommissionAffiliate from 'components/CalculateCommissionAffiliate';
import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import ModalActions from 'components/ModalActions';
import { API_MAP, agent, functions } from 'helpers';
import { constants } from 'helpers/constants';
import { EProductStatus, PRODUCT_STATUS_LABEL } from 'helpers/enums';
import DraftLabel from 'scenes/Producer/Product/components/DraftLabel';
import Alumy from 'scenes/Producer/Product/components/Partners/Alumy';
import Blinket from 'scenes/Producer/Product/components/Partners/Blinket';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { useAffiliatesProductStatusSummary } from 'v2/hooks/useAffiliatesProductStatusSummary';
import { useProductDeliveryMutation } from 'v2/hooks/useProductDeliveryMutation';
import { useProductInviteAffiliate } from 'v2/hooks/useProductInviteAffiliate';
import { useProductInvoices } from 'v2/hooks/useProductInvoices';
import { useProductStatusMutation } from 'v2/hooks/useProductStatusMutation';
import chatBotService, { Activity } from 'v2/services/chatBot';
import productService from 'v2/services/product';
import { ruleRunner, run } from 'validations/ruleRunner';
import { isEmail, required } from 'validations/rules';

import EditButton from './components/EditButton';
import QuickActions from './components/QuickActions';

const fieldValidations: Record<string, any> = {
  inputInviteAffiliate: ruleRunner('inputInviteAffiliate', 'e-mail', required, isEmail),
};

interface IProps {
  active: boolean;
  changeActive: any;
  onEditProduct: any;
  productData: any;
  activatedChildren: any;
}

const getStatusAmountByStatus = (affiliatesProductStatusSummary: any, status: string): number => {
  let statusAmount =
    Array.isArray(affiliatesProductStatusSummary) && affiliatesProductStatusSummary.find((x: any) => x.status == status)?.status_amount;

  if (!statusAmount) {
    statusAmount = 0;
  }

  return statusAmount;
};

const QuickView: React.FC<IProps> = ({ active, changeActive, onEditProduct, productData, activatedChildren }) => {
  const history = useHistory();

  const statusMutation = useProductStatusMutation();
  const inviteAffiliateMutation = useProductInviteAffiliate();
  const deliveryMutation = useProductDeliveryMutation();
  const invoicesQuery = useProductInvoices(productData?.id);
  const affiliatesSummaryQuery = useAffiliatesProductStatusSummary(productData?.id);

  const [loading, setLoading] = useState(true);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [idProductHistory, setIdProductHistory] = useState<number>(null);
  const [modalStatusProduct, setModalStatusProduct] = useState(false);
  const [modalStatusProductArchive, setModalStatusProductArchive] = useState(false);
  const [modalCloneProduct, setModalCloneProduct] = useState(false);
  const [statusProduct, setStatusProduct] = useState<'active' | 'inactive' | 'filed' | 'draft' | ' '>(null);
  const [loadingStatusArchive, setLoadingStatusArchive] = useState(false);
  const [loadingCloneProduct, setLoadingCloneProduct] = useState(false);
  const [inputInviteAffiliate, setInputInviteAffiliate] = useState<string>(null);
  const [inputDelivery, setInputDelivery] = useState<string>(null);
  const [inputDeliveryError, setInputDeliveryError] = useState(false);
  const [actionButtonDisabled, setActionButtonDisabled] = useState(false);
  const [numberAffiliates, setNumberAffiliates] = useState(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [hasDelivery, setHasDelivery] = useState(false);

  const productDataRef = useRef(productData);
  productDataRef.current = productData;

  const statusProductRef = useRef(statusProduct);
  statusProductRef.current = statusProduct;

  const inputInviteAffiliateRef = useRef(inputInviteAffiliate);
  inputInviteAffiliateRef.current = inputInviteAffiliate;

  const validationErrorsRef = useRef(validationErrors);
  validationErrorsRef.current = validationErrors;

  const prevActiveRef = useRef(active);

  const errorFor = useCallback((field: any) => {
    return validationErrors[field] || '';
  }, [validationErrors]);

  const isValid = useCallback((field: any) => {
    return !!validationErrors[field];
  }, [validationErrors]);

  const onValidation = useCallback((field: string) => {
    const currentErrors = { ...validationErrorsRef.current };
    const obj = run({ inputInviteAffiliate: inputInviteAffiliateRef.current }, [fieldValidations[field]]);

    currentErrors[field] = obj[field];

    for (const prop in currentErrors) {
      if (!currentErrors[prop]) {
        delete currentErrors[prop];
      }
    }

    setValidationErrors(currentErrors);
  }, []);

  const handleCloseQuickView = useCallback(() => {
    if (typeof changeActive === 'function') {
      changeActive();
    }

    setLoading(true);
    setIdProductHistory(productDataRef.current?.id);
  }, [changeActive]);

  const toggleModalStatusProduct = useCallback(() => {
    const product = productDataRef.current;

    if (Blinket.isBlinket(product.cnt_partner_cod)) {
      Blinket.blinketModal();
      return;
    }

    setModalStatusProduct((prev) => !prev);
  }, []);

  const onCancelModal = useCallback(() => {
    toggleModalStatusProduct();
  }, [toggleModalStatusProduct]);

  const onConfirmModal = useCallback(async () => {
    toggleModalStatusProduct();
    setActionButtonDisabled(true);

    try {
      await statusMutation.mutateAsync({
        productId: productDataRef.current.id,
        status: statusProductRef.current === 'active' ? EProductStatus.INACTIVE : EProductStatus.ACTIVE,
      });

      setStatusProduct(statusProductRef.current === 'active' ? 'inactive' : 'active');
      setActionButtonDisabled(false);

      toast.success('Produto alterado com sucesso');
      onEditProduct();
    } catch {
      setActionButtonDisabled(false);
      toast.error('Ops, erro ao atualizar o status do produto');
    }
  }, [statusMutation, onEditProduct, toggleModalStatusProduct]);

  const toggleModalStatusProductArchive = useCallback(() => {
    const product = productDataRef.current;

    if (Alumy.isAlumy(product.cnt_partner_cod)) {
      Alumy.alumyModal();
      return;
    }

    if (Blinket.isBlinket(product.cnt_partner_cod)) {
      Blinket.blinketModal();
      return;
    }

    setModalStatusProductArchive((prev) => !prev);
  }, []);

  const onCancelModalArchive = useCallback(() => {
    toggleModalStatusProductArchive();
  }, [toggleModalStatusProductArchive]);

  const onConfirmModalArchive = useCallback(async () => {
    setLoadingStatusArchive(true);
    setActionButtonDisabled(true);
    toggleModalStatusProductArchive();

    try {
      await statusMutation.mutateAsync({
        productId: productDataRef.current.id,
        status: EProductStatus.ARCHIVE,
      });

      setStatusProduct('filed');
      setLoadingStatusArchive(false);
      setActionButtonDisabled(false);

      onEditProduct();
      handleCloseQuickView();
    } catch {
      setLoadingStatusArchive(false);
      setActionButtonDisabled(false);
      toast.error('Ops, não foi possível excluir o produto');
    }
  }, [statusMutation, onEditProduct, toggleModalStatusProductArchive, handleCloseQuickView]);

  const toggleModalCloneProduct = useCallback(() => {
    const product = productDataRef.current;

    if (Blinket.isBlinket(product.cnt_partner_cod)) {
      Blinket.blinketModal();
      return;
    }

    setModalCloneProduct((prev) => !prev);
  }, []);

  const onCancelModalCloneProduct = useCallback(() => {
    toggleModalCloneProduct();
  }, [toggleModalCloneProduct]);

  const onConfirmModalCloneProduct = useCallback(async () => {
    toggleModalCloneProduct();
    setLoadingCloneProduct(true);
    setActionButtonDisabled(true);
    setCloneLoading(true);

    try {
      const result = await productService.cloneProduct(productDataRef.current.id);
      const clonedProduct = result?.product ?? result;
      history.push('/producer/product/editproduct/' + clonedProduct.id);
      functions.addScrollPage();
    } catch {
      toast.error('Ops, erro ao clonar o produto');
      setLoadingCloneProduct(false);
      setCloneLoading(false);
      setActionButtonDisabled(false);
    }
  }, [history, toggleModalCloneProduct]);

  const handleChange = useCallback((prop: any, event: any) => {
    if (prop === 'inputInviteAffiliate') {
      setInputInviteAffiliate(event.target.value);
      onValidation(prop);
    }

    if (prop === 'inputDelivery') {
      setInputDeliveryError(false);
      setInputDelivery(event.target.value);
    }
  }, [onValidation]);

  const handleClickInviteAffiliate = useCallback(async () => {
    const array = [];

    for (const prop in fieldValidations) {
      array.push(fieldValidations[prop]);
    }

    const errors = run({ inputInviteAffiliate: inputInviteAffiliateRef.current }, array);
    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      try {
        await inviteAffiliateMutation.mutateAsync({
          productId: productDataRef.current.id,
          email: inputInviteAffiliateRef.current,
        });

        setInputInviteAffiliate('');
        setNumberAffiliates((prev) => prev + 1);

        onEditProduct();
        toast.success('Sucesso, afiliado adicionado com êxito');
      } catch (error) {
        toast.error(error);
      }
    }
  }, [inviteAffiliateMutation, onEditProduct]);

  const handleClickDelivery = useCallback(async () => {
    const product = productDataRef.current;
    setInputDeliveryError(false);

    try {
      if (Alumy.isAlumy(product.cnt_partner_cod)) {
        await Alumy.alumyProductDelivery(product.id, inputDelivery);
      } else {
        await deliveryMutation.mutateAsync({
          productId: product.id,
          customerEmail: inputDelivery,
        });
      }

      setInputDelivery('');
      setInputDeliveryError(false);
      toast.success('Sucesso, arquivo entregue com êxito');
    } catch (error: any) {
      if (typeof error === 'string') {
        toast.error(error);
        return;
      }

      toast.error('Ops, erro na entrega manual');
    }
  }, [deliveryMutation, inputDelivery]);

  const handleClickGetInvoice = useCallback(async () => {
    if (!productDataRef.current) {
      return;
    }

    try {
      await invoicesQuery.refetch();
    } catch {
      toast.error('Ops, erro nos dados da fatura');
    }
  }, [invoicesQuery]);

  const handleClickGetAffiliatesProductStatusSummary = useCallback(async () => {
    if (!productDataRef.current) {
      return;
    }

    try {
      await affiliatesSummaryQuery.refetch();
    } catch {
      toast.error('Ops, erro nos dados dos afiliados');
    }
  }, [affiliatesSummaryQuery]);

  const handleChangeStatusProduct = useCallback(() => {
    toggleModalStatusProduct();
  }, [toggleModalStatusProduct]);

  const copiedCheckout = useCallback(() => {
    chatBotService.activity(Activity.copyCheckoutLink);
  }, []);

  useEffect(() => {
    const wasInactive = prevActiveRef.current === false;
    prevActiveRef.current = active;

    if (!wasInactive || !active) {
      return;
    }

    if (productData.id && productData.id !== idProductHistory) {
      setLoading(true);
      setInputDeliveryError(false);

      (async () => {
        const { data } = await agent.get(`${API_MAP.productHasDelivery}`.replace('{id}', productData.id));
        setLoading(false);
        setInputDelivery(idProductHistory === productData.id ? inputDelivery : '');
        setInputDeliveryError(false);
        setInputInviteAffiliate(idProductHistory === productData.id ? inputInviteAffiliate : '');
        setStatusProduct(productData.status);
        setNumberAffiliates(productData.affiliates);
        setHasDelivery(!!data);
      })();
    } else {
      setLoading(false);
      setActionButtonDisabled(false);
    }
  }, [active, productData, idProductHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!productData) {
    return (
      <Drawer
        onClose={handleCloseQuickView}
        placement='right'
        open={false}
        closable={false}
        styles={{ body: { padding: 0 }, wrapper: { maxWidth: '100%' } }}
        width={450}
      />
    );
  }

  const productDataReceive = productData;
  const physicalProductPending = productDataReceive.type === 3 && productDataReceive.moderation_status === 'P';
  const affiliatesSummaryData = affiliatesSummaryQuery.data;
  const approvedAffiliatesAmount = getStatusAmountByStatus(affiliatesSummaryData, 'A');
  const disapprovedAffiliatesAmount = getStatusAmountByStatus(affiliatesSummaryData, 'R');
  const pendingAffiliatesAmount = getStatusAmountByStatus(affiliatesSummaryData, 'P');
  const cantManualDelivery = productDataReceive.has_children || statusProduct === 'inactive' || !hasDelivery;

  const labelToggleStatus = statusMutation.isPending ? (
    <span style={{ position: 'relative', top: '3px' }}>
      <Spin size='small' />
    </span>
  ) : (
    PRODUCT_STATUS_LABEL[statusProduct]
  );

  const dontShowArchiveVariationTootip = productDataReceive.parent_product == 0 || activatedChildren > 2;

  return (
    <React.Fragment>
      <Drawer
        onClose={handleCloseQuickView}
        placement='right'
        open={active}
        styles={{ body: { padding: 0 }, wrapper: { maxWidth: '100%' } }}
        width={450}
        title={`Produto: ${productDataReceive.id}`}
      >
        {loading ? (
          <div className='loading flex h-full justify-center items-center' >
            <Spin size='large' />
          </div>
        ) : (
          <>
            <div className='p-4 flex flex-col gap-4'>
              {statusProduct !== 'filed' && (
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-1'>
                    {!Alumy.isAlumy(productDataReceive.cnt_partner_cod) &&
                      !productDataReceive.has_children &&
                      productDataReceive.type !== 2 && (
                        <PermissionHide action='myeduzz:meus-produtos:editar'>
                          <Button
                            id='quickview-product-copy-button'
                            size='small'
                            type='text'
                            onClick={toggleModalCloneProduct}
                            style={{ color: !actionButtonDisabled ? '#596375' : '#ddd', fontSize: 12 }}
                            disabled={actionButtonDisabled}
                            icon={loadingCloneProduct && cloneLoading ? <Spin size='small' /> : <CopyOutlined style={{ fontSize: 12, color: '#596375' }} />}
                          >
                            Copiar
                          </Button>
                        </PermissionHide>
                      )}

                    {productDataReceive.has_children && (productDataReceive.type === constants.INFO || productDataReceive.type === constants.PHYSICAL || productDataReceive.type === constants.TICKET) && (
                      <EditButton id={productDataReceive.id} parentId={productDataReceive.parent_product} onClick={functions.addScrollPage} disabled={actionButtonDisabled} />
                    )}

                    {!productDataReceive.has_children &&
                      (productDataReceive.type === constants.INFO || productDataReceive.type === constants.PHYSICAL || productDataReceive.type === constants.TICKET || productDataReceive.type === constants.SERVICE) && (
                        <EditButton id={productDataReceive.id} parentId={productDataReceive.parent_product} onClick={functions.addScrollPage} disabled={actionButtonDisabled} />
                      )}
                  </div>

                  <PermissionHide action='myeduzz:meus-produtos:editar'>
                    <Tooltip
                      title={dontShowArchiveVariationTootip ? '' : 'Ao excluir essa variação todo o Produto será excluído'}
                    >
                      <Button
                        id='quickview-product-delete-button'
                        size='small'
                        type='text'
                        danger
                        onClick={toggleModalStatusProductArchive}
                        style={{ fontSize: 12 }}
                        disabled={actionButtonDisabled}
                        icon={loadingStatusArchive && statusMutation.isPending ? <Spin size='small' /> : <DeleteOutlined style={{ fontSize: 12 }} />}
                      >
                        Excluir
                      </Button>
                    </Tooltip>
                  </PermissionHide>
                </div>
              )}

              {!!productDataReceive.todo && (
                <Alert type='warning' message={
                  <>
                    <strong>Atualize seu modelo de comissão para porcentagem</strong>
                    <p>Você ainda tem comissões por valor cadastradas em seus produtos!</p>
                    <p>Atualize suas comissões para o modelo de porcentagem.</p>
                  </>
                } />
              )}

              <div className='flex gap-3 items-start'>
                <div
                  className='w-20 h-20 rounded-lg bg-grey-100 bg-cover bg-center shrink-0'
                  style={{ backgroundImage: productDataReceive.image ? `url(${productDataReceive.image})` : undefined }}
                />

                <div className='flex flex-col gap-2 min-w-0 flex-1'>
                  <span
                    className='text-sm font-semibold text-text-heading truncate'
                    title={productDataReceive.title || 'Produto desconhecido'}
                  >
                    {productDataReceive.title || 'Produto desconhecido'}
                  </span>

                  {statusProduct !== 'draft' ? (
                    <label className='flex items-center gap-2 text-xs text-text-secondary'>
                      <Switch
                        size='small'
                        defaultChecked={statusProduct === 'active'}
                        checked={statusProduct === 'active'}
                        disabled={statusProduct === 'filed' || statusMutation.isPending}
                        onChange={handleChangeStatusProduct}
                      />
                      {labelToggleStatus}
                    </label>
                  ) : (
                    <div>
                      <DraftLabel />
                    </div>
                  )}

                  {!productDataReceive.has_children && (
                    <div className='flex items-baseline flex-col'>
                      <span className='text-[11px] uppercase tracking-wide text-text-secondary'>Preço</span>
                      <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores'>
                        <span className='text-lg font-bold text-success leading-none'>
                          {productDataReceive.charge_type === 'A' && (
                            <FormattedNumber value={productDataReceive.recurring_value} format='BRL' />
                          )}
                          {productDataReceive.charge_type === 'N' && (
                            <FormattedNumber value={productDataReceive.price} format='BRL' />
                          )}
                          {productDataReceive.charge_type === 'G' && 'Grátis'}
                          {(!productDataReceive.charge_type || productDataReceive.charge_type === '' || productDataReceive.charge_type === ' ') && (
                            <FormattedNumber value={0} format='BRL' />
                          )}
                        </span>
                      </PermissionHide>
                      <PermissionHide inverse={false} action='myeduzz:meus-produtos:ocultar-valores'>
                        <em className='text-text-secondary text-sm not-italic'>oculto</em>
                      </PermissionHide>
                      {productDataReceive.charge_type === 'A' && (
                        <span className='text-[11px] text-text-secondary'>recorrente</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!productDataReceive.has_children && (
                <div className='rounded-lg border border-grey-100 overflow-hidden'>
                  <div className='grid grid-cols-3 divide-x divide-grey-100'>
                    <div className='flex flex-col items-center gap-0.5 py-3 px-2'>
                      <span className='text-[11px] uppercase tracking-wide text-text-secondary'>Ganhos</span>
                      <span className='text-sm font-semibold text-success'>
                        <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores'>
                          <ColoredFormattedNumber value={productDataReceive.total_gains} format={productDataReceive.currency.code} />
                        </PermissionHide>
                        <PermissionHide inverse={false} action='myeduzz:meus-produtos:ocultar-valores'>
                          <em className='text-text-secondary font-normal not-italic'>oculto</em>
                        </PermissionHide>
                      </span>
                    </div>

                    <div className='flex flex-col items-center gap-0.5 py-3 px-2'>
                      <span className='text-[11px] uppercase tracking-wide text-text-secondary'>Afiliados</span>
                      <span className='text-sm font-semibold text-text-heading'>{numberAffiliates}</span>
                    </div>

                    <div className='flex flex-col items-center gap-0.5 py-3 px-2'>
                      <span className='text-[11px] uppercase tracking-wide text-text-secondary'>Funil Infinito</span>
                      <span className={`text-sm font-semibold ${productDataReceive.infinity_funnel ? 'text-success' : 'text-text-secondary'}`}>
                        {productDataReceive.infinity_funnel ? 'Ativado' : 'Desativado'}
                      </span>
                    </div>
                  </div>

                  <div className='border-t border-grey-100 grid grid-cols-3 divide-x divide-grey-100'>
                    <div className='flex flex-col items-center gap-0.5 py-3 px-2'>
                      <span className='text-[11px] uppercase tracking-wide text-text-secondary'>Comissão</span>
                      <span className='text-sm font-semibold text-text-heading'>
                        <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores'>
                          {productDataReceive.charge_type === 'A' && (
                            <CalculateCommissionAffiliate
                              price={productDataReceive.recurring_value}
                              commissionType={productDataReceive.recurring_comission_type}
                              commissionValue={productDataReceive.recurring_comission_value}
                            />
                          )}
                          {productDataReceive.charge_type === 'N' && (
                            <CalculateCommissionAffiliate
                              price={productDataReceive.price || 0}
                              commissionType={productDataReceive.comission_type}
                              commissionValue={productDataReceive.comission_value}
                            />
                          )}
                          {(!productDataReceive.charge_type || productDataReceive.charge_type === ' ' || productDataReceive.charge_type === '') && (
                            <CalculateCommissionAffiliate price={0} commissionValue={0} />
                          )}
                        </PermissionHide>
                        <PermissionHide inverse={false} action='myeduzz:meus-produtos:ocultar-valores'>
                          <em className='text-text-secondary font-normal not-italic'>oculto</em>
                        </PermissionHide>
                      </span>
                    </div>

                    <div className='flex flex-col items-center gap-0.5 py-3 px-2'>
                      <span className='text-[11px] uppercase tracking-wide text-text-secondary'>RPC/A</span>
                      <span className='text-sm font-semibold text-text-heading'>{productDataReceive.rpc_affiliate || 0}</span>
                    </div>

                    <div className='flex flex-col items-center gap-0.5 py-3 px-2'>
                      <span className='text-[11px] uppercase tracking-wide text-text-secondary'>RPC/P</span>
                      <span className='text-sm font-semibold text-text-heading'>{productDataReceive.rpc_producer || 0}</span>
                    </div>
                  </div>
                </div>
              )}

              <QuickActions
                productDataReceive={productDataReceive}
                statusProduct={statusProduct}
                physicalProductPending={physicalProductPending}
                copiedCheckout={copiedCheckout}
                handleChange={handleChange}
                inputInviteAffiliate={inputInviteAffiliate}
                productData={productData}
                isValid={isValid}
                handleClickInviteAffiliate={handleClickInviteAffiliate}
                isInvitingAffiliate={inviteAffiliateMutation.isPending}
                errorFor={errorFor}
                handleClickGetAffiliatesProductStatusSummary={handleClickGetAffiliatesProductStatusSummary}
                isLoadingAffiliatesSummary={affiliatesSummaryQuery.isFetching}
                approvedAffiliatesAmount={approvedAffiliatesAmount}
                disapprovedAffiliatesAmount={disapprovedAffiliatesAmount}
                pendingAffiliatesAmount={pendingAffiliatesAmount}
                cantManualDelivery={cantManualDelivery}
                inputDeliveryError={inputDeliveryError}
                inputDelivery={inputDelivery}
                handleClickDelivery={handleClickDelivery}
                isDelivering={deliveryMutation.isPending}
                handleClickGetInvoice={handleClickGetInvoice}
                productInvoice={invoicesQuery.data}
                isLoadingInvoice={invoicesQuery.isFetching}
              />
            </div>
          </>
        )}
      </Drawer>

      <ModalActions
        open={modalStatusProduct}
        onClose={onCancelModal}
        onOk={onConfirmModal}
        title={`Deseja realmente ${statusProduct === 'active' ? 'inativar' : 'ativar'} este produto? `}
        buttonOk={statusProduct === 'active' ? 'Inativar' : 'Ativar'}
      >
        {
          (statusProduct === 'active' && productDataReceive.has_children)
            ? 'Atenção, enquanto o produto pai estiver inativo todas suas variações também ficarão inativas no checkout.'
            : ''
        }
      </ModalActions>

      <ModalActions
        open={modalStatusProductArchive}
        onClose={onCancelModalArchive}
        onOk={onConfirmModalArchive}
        title='Deseja realmente excluir este produto?'
        buttonOk={'Excluir'}
        buttonOkColor={'#dc3f53'}
      >
        <p>{`Atenção, esta operação é irreversível${productData.has_children ? ' e automaticamente todas variações serão excluídas.' : '.'}` } </p>
      </ModalActions>

      <ModalActions
        open={modalCloneProduct}
        onClose={onCancelModalCloneProduct}
        onOk={onConfirmModalCloneProduct}
        title={'Deseja realmente duplicar este produto?'}
        buttonOk={'Duplicar'}
      >
        <p>Obs: você será redirecionado para a tela de edição.</p>
      </ModalActions>
    </React.Fragment>
  );
};

export default QuickView;
